import { useState, useLayoutEffect } from 'react'
import { Outlet, Link, useNavigate } from 'react-router-dom'
import { Menu, X, Award } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { ASILIFLOW_DEMO_CONTACT_PATH, ASILIFLOW_SUPPORT_MAILTO } from '@/config/contact'
import Logo from '@/components/shared/Logo'

const publicNavItems = [
  { id: 'transformation', label: 'Transformation' },
  { id: 'features', label: 'Features' },
  { id: 'how-it-works', label: 'How It Works' },
  { id: 'pricing', label: 'Pricing' },
]

export default function PublicLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { user, signOut } = useAuth()
  const navigate = useNavigate()

  // Force light mode on public pages — useLayoutEffect runs synchronously
  // before paint, preventing a flash of dark-mode styling on navigation.
  useLayoutEffect(() => {
    const root = document.documentElement
    const wasDark = root.classList.contains('dark')
    root.classList.remove('dark')
    root.style.colorScheme = 'light'

    return () => {
      if (wasDark) {
        root.classList.add('dark')
        root.style.colorScheme = 'dark'
      }
    }
  }, [])

  const handleSignOut = async () => {
    try {
      await signOut()
      navigate('/')
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
      setMobileMenuOpen(false)
    }
  }

  return (
    <div
      className="public-layout min-h-screen bg-white flex flex-col"
      data-theme="light"
      style={{ colorScheme: 'light' }}
    >
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <Logo size="lg" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {publicNavItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-sm text-gray-600 hover:text-gray-900 transition"
                >
                  {item.label}
                </button>
              ))}
              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    className="text-sm text-gray-600 hover:text-gray-900 transition"
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-sm text-gray-600 hover:text-gray-900 transition"
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition"
                  >
                    Start 30-Day Trial
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-50 transition"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-600" />
              ) : (
                <Menu className="w-6 h-6 text-gray-600" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <div className="px-4 py-3 space-y-3">
              {publicNavItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="block w-full text-left text-sm text-gray-600 hover:text-gray-900 py-2"
                >
                  {item.label}
                </button>
              ))}
              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    className="block text-sm text-gray-600 hover:text-gray-900 py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={() => {
                      handleSignOut()
                      setMobileMenuOpen(false)
                    }}
                    className="block w-full text-left text-sm text-gray-600 hover:text-gray-900 py-2"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="block text-sm text-gray-600 hover:text-gray-900 py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    className="block text-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Start 30-Day Trial
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="pt-16 flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-8">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">AsiliFlow</h3>
              <p className="text-sm text-gray-600 mb-4">
                Healthcare operations, simplified.
              </p>
              <div className="flex gap-3">
                <Award className="w-5 h-5 text-primary-600" />
                <span className="text-xs text-gray-500">
                  Built for regulated healthcare organizations
                </span>
              </div>
            </div>

            {/* Product */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Product</h4>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => scrollToSection('transformation')}
                    className="text-sm text-gray-600 hover:text-gray-900 transition"
                  >
                    Transformation
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection('features')}
                    className="text-sm text-gray-600 hover:text-gray-900 transition"
                  >
                    Features
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection('how-it-works')}
                    className="text-sm text-gray-600 hover:text-gray-900 transition"
                  >
                    How It Works
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection('pricing')}
                    className="text-sm text-gray-600 hover:text-gray-900 transition"
                  >
                    Pricing
                  </button>
                </li>
                <li>
                  <Link
                    to={ASILIFLOW_DEMO_CONTACT_PATH}
                    className="text-sm text-gray-600 hover:text-gray-900 transition"
                  >
                    Book Demo
                  </Link>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Company</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/about"
                    className="text-sm text-gray-600 hover:text-gray-900 transition"
                  >
                    About Us
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="text-sm text-gray-600 hover:text-gray-900 transition"
                  >
                    Contact Us
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy"
                    className="text-sm text-gray-600 hover:text-gray-900 transition"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className="text-sm text-gray-600 hover:text-gray-900 transition"
                  >
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Support</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/help"
                    className="text-sm text-gray-600 hover:text-gray-900 transition"
                  >
                    Help Center
                  </Link>
                </li>
                <li>
                  <a
                    href={ASILIFLOW_SUPPORT_MAILTO}
                    className="text-sm text-gray-600 hover:text-gray-900 transition"
                  >
                    Email Support
                  </a>
                </li>
                <li>
                  <Link
                    to="/docs"
                    className="text-sm text-gray-600 hover:text-gray-900 transition"
                  >
                    Documentation
                  </Link>
                </li>
                <li>
                  <Link
                    to="/status"
                    className="text-sm text-gray-600 hover:text-gray-900 transition"
                  >
                    System Status
                  </Link>
                </li>
              </ul>
            </div>

            {/* Affiliates */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Affiliates</h4>
              <ul className="space-y-2">
                <li>
                  <a
                    href="https://firstcallhealthstaffing.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-gray-600 hover:text-gray-900 transition"
                  >
                    First Call Health Staffing
                  </a>
                </li>
                <li>
                  <a
                    href="https://asiliguide.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-gray-600 hover:text-gray-900 transition"
                  >
                    AsiliGuide Platform
                  </a>
                </li>
                <li>
                  <a
                    href="https://snapshot.asiliguide.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-gray-600 hover:text-gray-900 transition"
                  >
                    AsiliGuide Snapshot
                  </a>
                </li>
                <li>
                  <a
                    href="https://asiliguide.com/compliance/states"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-gray-600 hover:text-gray-900 transition"
                  >
                    State Compliance
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4 text-center sm:text-left">
            <p className="text-xs sm:text-sm text-gray-500">
              © {new Date().getFullYear()} AsiliFlow, a product of Asili LLC. All rights reserved.
            </p>
            <div className="flex items-center gap-4 sm:gap-6">
              <Link
                to="/privacy"
                className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition"
              >
                Privacy
              </Link>
              <Link
                to="/terms"
                className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition"
              >
                Terms
              </Link>
              <a
                href="#"
                className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition"
              >
                Security
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
